import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, AlertCircle, Users, MessageSquare, Paperclip, Calendar } from 'lucide-react';
import { Task, Column, User } from '../types';
import UserAssignment from './UserAssignment';
import Comments from './Comments';
import Attachments from './Attachments';

interface AddTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (taskData: Partial<Task>) => void;
  editingTask: Task | null;
  columns: Record<string, Column>;
  users: Record<string, User>;
  onAssignUser?: (taskId: string, userId: string) => void;
  onUnassignUser?: (taskId: string, userId: string) => void;
  onAddComment?: (taskId: string, content: string) => void;
  onDeleteComment?: (taskId: string, commentId: string) => void;
  onAddAttachment?: (taskId: string, file: File) => void;
  onDeleteAttachment?: (taskId: string, attachmentId: string) => void;
}

const AddTaskModal: React.FC<AddTaskModalProps> = ({ isOpen, onClose, onSave, editingTask, columns }) => {
  const [formData, setFormData] = useState<Partial<Task>>({
    title: '',
    content: '',
    priority: 'medium',
    columnId: 'todo'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editingTask) {
      setFormData({
        title: editingTask.title || '',
        content: editingTask.content || '',
        priority: editingTask.priority || 'medium',
        columnId: Object.keys(columns).find(colId =>
          columns[colId].taskIds.includes(editingTask.id)
        ) || 'todo'
      });
    } else {
      setFormData({
        title: '',
        content: '',
        priority: 'medium',
        columnId: 'todo'
      });
    }
    setErrors({});
  }, [editingTask, columns, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    if (formData.title && formData.title.trim().length < 3) {
      newErrors.title = 'Title must be at least 3 characters';
    }
    if (formData.content && formData.content.length > 500) {
      newErrors.content = 'Description must be less than 500 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSave({
      ...formData,
      title: formData.title?.trim(),
      content: formData.content?.trim(),
      priority: formData.priority,
      columnId: formData.columnId
    });
  };

  const handleInputChange = (field: keyof Task, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingTask ? 'Edit Task' : 'Add New Task'}
              </h2>
              <motion.button
                onClick={onClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </motion.button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className={`kanban-input ${errors.title ? 'border-red-300' : ''}`}
                  placeholder="Enter task title..."
                  required
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                    <AlertCircle className="w-3 h-3" />
                    <span>{errors.title}</span>
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.content || ''}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  className="kanban-input resize-none"
                  rows={3}
                  placeholder="Enter task description..."
                />
                {errors.content && (
                  <p className="text-red-500 text-sm mt-1 flex items-center space-x-1">
                    <AlertCircle className="w-3 h-3" />
                    <span>{errors.content}</span>
                  </p>
                )}
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority || 'medium'}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="kanban-input"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Column (only show when adding new task) */}
              {!editingTask && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Column
                  </label>
                  <select
                    value={formData.columnId || 'todo'}
                    onChange={(e) => handleInputChange('columnId', e.target.value)}
                    className="kanban-input"
                  >
                    {Object.values(columns).map(column => (
                      <option key={column.id} value={column.id}>
                        {column.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3 pt-4">
                <motion.button
                  type="button"
                  onClick={onClose}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 kanban-button-secondary"
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 kanban-button-primary flex items-center justify-center space-x-2"
                  disabled={!formData.title?.trim()}
                >
                  <Save className="w-4 h-4" />
                  <span>{editingTask ? 'Update' : 'Add'} Task</span>
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddTaskModal;
